"use client";

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useParams } from "next/navigation";
import { useStore as useZustandStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";
import type { SessionLogEntry } from "@/app/session/[sessionId]/_components/SessionLogOverlay";
import type {
  SessionSummary,
  UserSessionItem,
  WorkspaceDrawerPanel,
} from "@/app/session/[sessionId]/sessionView";
import type { MediaPlaylist } from "@/hooks/useMediaLibrary";

const MAX_SESSION_ROUTE_LOG_ENTRIES = 80;
const MAX_CHART_SAMPLES = 1200;
const MAX_PENDING_SAMPLES = 5000;

type SessionActionHandler = () => void | Promise<void>;
type WorkspaceDrawerPanelUpdate =
  | WorkspaceDrawerPanel
  | null
  | ((current: WorkspaceDrawerPanel | null) => WorkspaceDrawerPanel | null);

export type SensorSample = {
  timestamp: number;
  gsrRawAdc: number | null;
  gsrSmoothAdc: number | null;
  gsrUiLevel: number | null;
  trackUri: string | null;
  trackPositionMs: number | null;
  mediaProvider?: string | null;
  mediaTrackId?: string | null;
  trackName?: string | null;
  artistNames?: string | null;
  imageUrl?: string | null;
  albumName?: string | null;
  durationMs?: number | null;
  previewUrl?: string | null;
  isRestricted?: boolean | null;
  restrictionReason?: string | null;
  restrictionReasons?: string[] | null;
};

export type SessionRouteStoreState = {
  current: {
    gsrRawAdc: number | null;
    gsrSmoothAdc: number | null;
    gsrUiLevel: number | null;
  };
  chartHistory: SensorSample[];
  pending: SensorSample[];
  recording: boolean;
  selectedPlaylist: MediaPlaylist | null;
  playlistRefreshToken: number;
  playlistSummaryView: "playlist" | "track";
  workspaceDrawerPanel: WorkspaceDrawerPanel | null;
  sessionLogOverlayOpen: boolean;
  sessionLogAlertCount: number;
  exportAllUserDataHandler: SessionActionHandler | null;
  sessionLogEntries: SessionLogEntry[];
  copiedSessionId: string | null;
  inspectorSessionId: string | null;
  inspectorSummary: SessionSummary | null;
  inspectorSummaryLoading: boolean;
  inspectorSummaryError: string | null;
  userSessions: UserSessionItem[];
  userSessionsLoading: boolean;
  userSessionsError: string | null;
  endSessionConfirmOpen: boolean;
};

export type SessionRouteStore = SessionRouteStoreState & {
  ingestSample: (data: Omit<SensorSample, "timestamp">) => void;
  takePendingBatch: (max?: number) => SensorSample[];
  setRecording: (recording: boolean) => void;
  clearData: () => void;
  setSelectedPlaylist: (playlist: MediaPlaylist | null) => void;
  bumpPlaylistRefresh: () => void;
  setPlaylistSummaryView: (view: "playlist" | "track") => void;
  setWorkspaceDrawerPanel: (panel: WorkspaceDrawerPanelUpdate) => void;
  setSessionLogOverlayOpen: (open: boolean) => void;
  setSessionLogAlertCount: (count: number) => void;
  registerExportAllUserDataHandler: (handler: SessionActionHandler | null) => void;
  requestExportAllUserData: () => void;
  setSessionLogEntries: (entries: SessionLogEntry[]) => void;
  appendSessionLog: (entry: SessionLogEntry) => void;
  clearSessionLog: () => void;
  setCopiedSessionId: (sessionId: string | null) => void;
  setInspectorState: (
    patch: Partial<
      Pick<
        SessionRouteStoreState,
        | "inspectorSessionId"
        | "inspectorSummary"
        | "inspectorSummaryLoading"
        | "inspectorSummaryError"
      >
    >
  ) => void;
  setUserSessionsState: (
    patch: Partial<
      Pick<
        SessionRouteStoreState,
        "userSessions" | "userSessionsLoading" | "userSessionsError"
      >
    >
  ) => void;
  setEndSessionConfirmOpen: (open: boolean) => void;
  resetSessionRouteStore: () => void;
};

export type SessionRouteStoreApi = StoreApi<SessionRouteStore>;

export type SessionRouteStoreProviderProps = {
  children: ReactNode;
  initialState?: Partial<SessionRouteStoreState>;
  storeKey?: string;
};

function normalizeAlertCount(count: number) {
  return Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
}

function normalizeSessionLogEntries(entries: SessionLogEntry[]) {
  return entries.slice(0, MAX_SESSION_ROUTE_LOG_ENTRIES);
}

function createDefaultSensorDataState() {
  return {
    current: {
      gsrRawAdc: null,
      gsrSmoothAdc: null,
      gsrUiLevel: null,
    },
    chartHistory: [] as SensorSample[],
    pending: [] as SensorSample[],
    recording: false,
  };
}

function createDefaultSessionRouteState(
  initialState: Partial<SessionRouteStoreState> = {}
): SessionRouteStoreState {
  return {
    current: initialState.current ?? createDefaultSensorDataState().current,
    chartHistory: initialState.chartHistory ?? [],
    pending: initialState.pending ?? [],
    recording: initialState.recording ?? false,
    selectedPlaylist: initialState.selectedPlaylist ?? null,
    playlistRefreshToken: initialState.playlistRefreshToken ?? 0,
    playlistSummaryView: initialState.playlistSummaryView ?? "playlist",
    workspaceDrawerPanel: initialState.workspaceDrawerPanel ?? null,
    sessionLogOverlayOpen: initialState.sessionLogOverlayOpen ?? false,
    sessionLogAlertCount: normalizeAlertCount(
      initialState.sessionLogAlertCount ?? 0
    ),
    exportAllUserDataHandler: initialState.exportAllUserDataHandler ?? null,
    sessionLogEntries: normalizeSessionLogEntries(
      initialState.sessionLogEntries ?? []
    ),
    copiedSessionId: initialState.copiedSessionId ?? null,
    inspectorSessionId: initialState.inspectorSessionId ?? null,
    inspectorSummary: initialState.inspectorSummary ?? null,
    inspectorSummaryLoading: initialState.inspectorSummaryLoading ?? false,
    inspectorSummaryError: initialState.inspectorSummaryError ?? null,
    userSessions: initialState.userSessions ?? [],
    userSessionsLoading: initialState.userSessionsLoading ?? false,
    userSessionsError: initialState.userSessionsError ?? null,
    endSessionConfirmOpen: initialState.endSessionConfirmOpen ?? false,
  };
}

/**
 * Route-scoped store for `/session/*` UI state.
 *
 * This is the seam the frontend is currently missing: session-only state that
 * is shared by the session header, the session page, and route-local drawers /
 * overlays should live here instead of the root app store.
 */
export function createSessionRouteStore(
  initialState: Partial<SessionRouteStoreState> = {}
): SessionRouteStoreApi {
  const defaultState = createDefaultSessionRouteState(initialState);

  return createStore<SessionRouteStore>()((set, get) => ({
    ...defaultState,

    ingestSample: (data) => {
      const timestamp = Date.now();
      const sample: SensorSample = { timestamp, ...data };

      set((state) => {
        const current = {
          gsrRawAdc: sample.gsrRawAdc ?? state.current.gsrRawAdc,
          gsrSmoothAdc: sample.gsrSmoothAdc ?? state.current.gsrSmoothAdc,
          gsrUiLevel: sample.gsrUiLevel ?? state.current.gsrUiLevel,
        };

        if (!state.recording) {
          return { current };
        }

        return {
          current,
          chartHistory: [...state.chartHistory, sample].slice(-MAX_CHART_SAMPLES),
          pending: [...state.pending.slice(-MAX_PENDING_SAMPLES + 1), sample],
        };
      });
    },
    takePendingBatch: (max) => {
      const { pending } = get();
      if (pending.length === 0) return [];

      const count = max ? Math.min(max, pending.length) : pending.length;
      const batch = pending.slice(0, count);
      set({ pending: pending.slice(count) });
      return batch;
    },
    setRecording: (recording) => set({ recording }),
    clearData: () => set(createDefaultSensorDataState()),
    setSelectedPlaylist: (selectedPlaylist) => set({ selectedPlaylist }),
    bumpPlaylistRefresh: () =>
      set((state) => ({
        playlistRefreshToken: state.playlistRefreshToken + 1,
      })),
    setPlaylistSummaryView: (playlistSummaryView) =>
      set({ playlistSummaryView }),
    setWorkspaceDrawerPanel: (panel) =>
      set((state) => ({
        workspaceDrawerPanel:
          typeof panel === "function"
            ? panel(state.workspaceDrawerPanel)
            : panel,
      })),
    setSessionLogOverlayOpen: (sessionLogOverlayOpen) =>
      set({ sessionLogOverlayOpen }),
    setSessionLogAlertCount: (sessionLogAlertCount) =>
      set({ sessionLogAlertCount: normalizeAlertCount(sessionLogAlertCount) }),
    registerExportAllUserDataHandler: (exportAllUserDataHandler) =>
      set({ exportAllUserDataHandler }),
    requestExportAllUserData: () => {
      const handler = get().exportAllUserDataHandler;
      if (!handler) return;
      void Promise.resolve().then(() => handler());
    },
    setSessionLogEntries: (sessionLogEntries) =>
      set({
        sessionLogEntries: normalizeSessionLogEntries(sessionLogEntries),
      }),
    appendSessionLog: (entry) =>
      set((state) => ({
        sessionLogEntries: normalizeSessionLogEntries([
          entry,
          ...state.sessionLogEntries,
        ]),
      })),
    clearSessionLog: () => set({ sessionLogEntries: [] }),
    setCopiedSessionId: (copiedSessionId) => set({ copiedSessionId }),
    setInspectorState: (patch) =>
      set((state) => ({
        inspectorSessionId:
          patch.inspectorSessionId !== undefined
            ? patch.inspectorSessionId
            : state.inspectorSessionId,
        inspectorSummary:
          patch.inspectorSummary !== undefined
            ? patch.inspectorSummary
            : state.inspectorSummary,
        inspectorSummaryLoading:
          patch.inspectorSummaryLoading !== undefined
            ? patch.inspectorSummaryLoading
            : state.inspectorSummaryLoading,
        inspectorSummaryError:
          patch.inspectorSummaryError !== undefined
            ? patch.inspectorSummaryError
            : state.inspectorSummaryError,
      })),
    setUserSessionsState: (patch) =>
      set((state) => ({
        userSessions:
          patch.userSessions !== undefined
            ? patch.userSessions
            : state.userSessions,
        userSessionsLoading:
          patch.userSessionsLoading !== undefined
            ? patch.userSessionsLoading
            : state.userSessionsLoading,
        userSessionsError:
          patch.userSessionsError !== undefined
            ? patch.userSessionsError
            : state.userSessionsError,
      })),
    setEndSessionConfirmOpen: (endSessionConfirmOpen) =>
      set({ endSessionConfirmOpen }),
    resetSessionRouteStore: () => set(createDefaultSessionRouteState()),
  }));
}

const SessionRouteStoreContext = createContext<SessionRouteStoreApi | null>(
  null
);

export function SessionRouteStoreProvider({
  children,
  initialState,
  storeKey,
}: SessionRouteStoreProviderProps) {
  const params = useParams<{ sessionId?: string }>();
  const routeSessionId =
    typeof params?.sessionId === "string" && params.sessionId.trim()
      ? params.sessionId
      : null;
  const effectiveStoreKey = storeKey ?? routeSessionId ?? "session-route";
  const storeEntry = useMemo(
    () => ({
      key: effectiveStoreKey,
      store: createSessionRouteStore(initialState),
    }),
    [effectiveStoreKey, initialState]
  );

  return (
    <SessionRouteStoreContext.Provider value={storeEntry.store}>
      {children}
    </SessionRouteStoreContext.Provider>
  );
}

export function useSessionRouteStore<T>(
  selector: (state: SessionRouteStore) => T
) {
  const store = useContext(SessionRouteStoreContext);
  if (!store) {
    throw new Error(
      "useSessionRouteStore must be used within SessionRouteStoreProvider"
    );
  }
  return useZustandStore(store, selector);
}

export function useSessionRouteStoreApi() {
  const store = useContext(SessionRouteStoreContext);
  if (!store) {
    throw new Error(
      "useSessionRouteStoreApi must be used within SessionRouteStoreProvider"
    );
  }
  return store;
}
